import { useEditorEditableSync } from '@/components/tiptap/hooks';
import { CdiNudgeHighlights } from '@/components/tiptap/shared/cdi-nudge-highlights/extension';
import { MedicationHighlights } from '@/components/tiptap/shared/medication-highlights/extension';
import { Dictation } from '@/components/tiptap/sharedExtensions/dictation/dictation';
import { DictationCursor } from '@/components/tiptap/sharedExtensions/dictation/dictation-cursor';
import { NoteSuggestionHighlight } from '@/components/tiptap/sharedExtensions/note-suggestion-highlight';
import { EmphasisShortcutGate } from '@/components/tiptap/sharedExtensions/shortcuts/emphasis-shortcut-gate';
import { EscapeToFocusOut } from '@/components/tiptap/sharedExtensions/shortcuts/escape-to-focus-out';
import { KeydownSelectionGuard } from '@/components/tiptap/sharedExtensions/shortcuts/keydown-selection-guard';
import { toNoteEditorSurface } from '@/components/tiptap/telemetry/emit-note-editor-render-failed';
import { EditorType } from '@/components/tiptap/types/index.enums';
import { ShimmerExtension } from '@/components/tiptap/v1/extensions/text-shimmer/text-shimmer';
import { withCanvasDelayedDictateHint } from '@/features/dictation/reengagement/canvas-delayed-dictate-placeholder';
import { useIsomorphicLayoutEffect } from '@/hooks/useIsomorphicLayoutEffect';
import {
  useEditorLifecycleTelemetry,
  type EditorTelemetrySurface,
} from '@/utils/telemetry/editor-observability';
import { StreamedTextFade } from '@heidi/editor/react';
import NodeRange from '@tiptap/extension-node-range';
import { type PlaceholderOptions } from '@tiptap/extension-placeholder';
import { Color, TextStyle } from '@tiptap/extension-text-style';
import { useEditor, type AnyExtension } from '@tiptap/react';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { buildMarkdownNativeExtensions } from './extension-kit';
import { ClipboardSerializer } from './extensions/clipboard';
import { DiffDecorationsExtension } from './extensions/diff-decorations';
import { EntityVerificationHighlight } from './extensions/entity-verification-highlight';
import { SlashCommand } from './extensions/slash-command';
import {
  useMarkdownNativeContentSync,
  type MarkdownNativeContentType,
} from './hooks/use-markdown-native-content-sync';
import { NoteEvidenceHighlight } from './note-evidence-highlight';
import { type MarkdownSectionNodeStorage } from './section-node/section-node';

/**
 * The markdown-native note editor: hydrates from markdown (or HTML for
 * legacy notes — lazy migration), saves markdown via `editor.getMarkdown()`.
 */
export const useMarkdownNativeEditor = ({
  content,
  contentType = 'markdown',
  surface,
  editable,
  isStreaming = false,
  fadeStreamedText = false,
  forceApply = false,
  autofocus = false,
  placeholder,
  showPlaceholderWhenDisabled = false,
  editorType,
  codingOverlayExtension,
}: {
  content: string;
  /** Hydration format of `content` — `'html'` only for legacy notes. */
  contentType?: MarkdownNativeContentType;
  /**
   * Lifecycle membership. `document` and `template` are lifecycle-only — they
   * are not part of the note-tab render-success SLI.
   */
  surface?: EditorTelemetrySurface;
  editable: boolean;
  /** Throttles content sync to one update per animation frame during SSE streaming. */
  isStreaming?: boolean;
  /** Fades newly appended stream blocks; off by default for every other consumer. */
  fadeStreamedText?: boolean;
  /** Applies the current `content` even while focused (genuine external insert). */
  forceApply?: boolean;
  /** Focuses the editor when it is created. */
  autofocus?: boolean;
  placeholder?: PlaceholderOptions['placeholder'];
  /** Keeps the placeholder visible while the editor is not editable. */
  showPlaceholderWhenDisabled?: boolean;
  /**
   * Host editor type, stamped onto `MarkdownSectionNode` storage so the
   * editor-driven creation paths (slash command, convert-to-section) report the
   * correct `editor_context` on `section_created`.
   */
  editorType?: EditorType;
  /**
   * The PBC coding-overlay extension, pre-built by the caller (`NoteTabPane`)
   * with the session's current problems. Present whenever the markdown-native
   * editor is enabled — inert (`problems: []`) for a non-PBC note rather than
   * `undefined`, so its presence never flips with `problemBasedCharting` and the
   * editor is never torn down; `undefined` only when the caller does not build
   * one at all. It is built upstream because neither this file nor
   * `buildMarkdownNativeExtensions` may import `@heidi/coding`: the
   * `no-new-legacy-coding-files` ESLint ratchet only allow-lists a fixed set of
   * pre-existing files, and nothing under `markdown-native/` is on it. It is
   * appended at the `useEditor` call below, not the `extensions` memo's array
   * literal above, which a source-inspecting test (see the "stack freeze"
   * comment above) parses structurally.
   */
  codingOverlayExtension?: AnyExtension;
}) => {
  const intl = useIntl();
  const shouldSeedEmptyDocumentForPlaceholder =
    showPlaceholderWhenDisabled && contentType === 'markdown' && content.length === 0;
  const resolvedPlaceholder =
    placeholder ??
    intl.formatMessage({
      defaultMessage: 'Click here to start writing …',
      id: '+3ED5N91MB',
    });

  // Live-editor-only extensions, kept out of `buildMarkdownNativeExtensions` so
  // the kit stays the minimal serialization contract the headless harness and
  // section derivation run against.
  //   - `SlashCommand` / `Dictation` / `DictationCursor` / `ShimmerExtension` /
  //     `ClipboardSerializer` are DOM/React UX (a floating `/` popup, transcription
  //     insertion, a recording cursor, smart-cleanup streaming, copy/cut clipboard
  //     handling) with no place in headless.
  //   - `TextStyle` + `Color` are pure schema marks, not DOM/React — they live
  //     here only because dictation's provisional text is the only thing that
  //     applies them, transiently. They define no markdown serialization, so
  //     `getMarkdown()` drops them and the kit/headless contract is unchanged;
  //     finalize strips them anyway so they never reach saved markdown.
  //     (vMarkdown registers them in its live hook the same way.)
  //   - `NoteSuggestionHighlight` decorates AI term-swap suggestions and mounts
  //     the accept/reject popover. Pure ProseMirror decorations + a body-level
  //     widget — no schema/markdown role, so it stays out of the kit.
  //   - `NoteEvidenceHighlight` is a transient overlay mark applied by
  //     `useApplyNoteEvidence` (reveal-evidence-in-note); it defines no markdown
  //     serialization, so `getMarkdown()` drops it — kit/headless contract unchanged.
  //   - `EscapeToFocusOut` is a keyboard-shortcut-only extension (Escape blurs the
  //     editor) with no schema or serialization role — same placement as vMarkdown.
  //   - `KeydownSelectionGuard` is a keydown-only plugin (re-syncs a stray DOM
  //     selection) with no schema or serialization role — same placement as vMarkdown.
  //   - `NodeRange` (key: Shift) is Shift-based multi-block range selection — a
  //     selection behavior with no schema or serialization role, so it stays out
  //     of the headless kit — same placement and config as vMarkdown.
  const extensions = useMemo(() => {
    const canvasPlaceholder = withCanvasDelayedDictateHint(resolvedPlaceholder);
    const extensionKitOptions = {
      placeholder: canvasPlaceholder,
      showPlaceholderWhenDisabled,
    };
    // Precomputed so the array member below stays single-line: the live-only
    // stack freeze reads this array literal from source and treats any line
    // that opens with an identifier as a member.
    const copySource =
      editorType === EditorType.TEMPLATE ? 'template_editor' : 'editor_clipboard';

    return [
      ...buildMarkdownNativeExtensions(extensionKitOptions),
      SlashCommand,
      EmphasisShortcutGate,
      ClipboardSerializer.configure({ copySource }),
      Dictation,
      DictationCursor,
      TextStyle,
      Color.configure({ types: ['textStyle'] }),
      ShimmerExtension,
      NoteSuggestionHighlight,
      DiffDecorationsExtension,
      NoteEvidenceHighlight,
      EscapeToFocusOut,
      KeydownSelectionGuard,
      NodeRange.configure({ key: 'Shift' }),
      // Clinical-safety highlighting of verified/flagged entities + hover
      // tooltip. Decoration-only (no markdown serialization), so it stays out
      // of the headless kit — same placement as vMarkdown's live hook.
      EntityVerificationHighlight,
      // Installed unconditionally: the editor is created ONCE (`useEditor(…, [])`)
      // and flag reads resolve async — a flag-conditional extension list freezes
      // the pre-resolution `false` forever. The plugin is decoration-only and
      // fully dormant until `useStreamedTextFade` activates it, and that
      // activation is where `fadeStreamedText` is enforced (reactively).
      StreamedTextFade,
      // In-note medication detection overlays. Decoration-only.
      MedicationHighlights,
      CdiNudgeHighlights,
    ];
  }, [resolvedPlaceholder, showPlaceholderWhenDisabled, editorType]);

  // Appended outside the memoized `extensions` array above (not spread into
  // it) so the source-inspecting "stack freeze" test that parses that literal
  // stays untouched. NoteTabPane now hands the overlay down at first mount and
  // never removes it — a non-PBC note gets an inert overlay, not `undefined`
  // (see NoteTabPane's `markdownNativeCodingOverlayExtension`) — so the extension
  // is present when the editor is created. The `useEditor` deps below stay `[]`:
  // the editor is created ONCE and MUST NOT be recreated when
  // `problemBasedCharting` flips, or undo/redo history is lost and keystrokes
  // inside the autosave debounce revert. Ongoing problem updates reach this same
  // editor instance via `useCodingOverlaySync`, not by rebuilding the extension.
  const extensionsWithCodingOverlay = codingOverlayExtension
    ? [...extensions, codingOverlayExtension]
    : extensions;

  const editor = useEditor(
    {
      immediatelyRender: true,
      shouldRerenderOnTransaction: false,
      extensions: extensionsWithCodingOverlay,
      content: shouldSeedEmptyDocumentForPlaceholder
        ? { type: 'doc', content: [{ type: 'paragraph' }] }
        : content,
      contentType: shouldSeedEmptyDocumentForPlaceholder ? undefined : contentType,
      editable,
      autofocus,
      editorProps: {
        attributes: {
          class: 'flex-1',
        },
      },
    },
    []
  );

  useEditorLifecycleTelemetry({
    editor,
    variant: 'markdown_native',
    surface: surface ?? 'note',
  });
  useMarkdownNativeContentSync({
    editor,
    content,
    contentType,
    surface: toNoteEditorSurface(surface),
    isStreaming,
    fadeStreamedText,
    forceApply,
  });
  useEditorEditableSync(editor, editable);

  // Stamp the editor type onto MarkdownSectionNode storage so the creation paths
  // that run outside React (slash command, convert-to-section) can report
  // `editor_context`. Layout effect so the stamp lands before paint — before any
  // keystroke could fire a section-creating action.
  useIsomorphicLayoutEffect(() => {
    const storage = (
      editor?.storage as unknown as
        | Record<string, MarkdownSectionNodeStorage | undefined>
        | undefined
    )?.sectionNode;
    if (storage) storage.editorType = editorType;
  }, [editor, editorType]);

  return { editor };
};
